/**
 * Operator write actions of the Control Center:
 *
 * - registering a domain for a customer (subsystems/registrar/facade/adapters/admin-api/src/admin/domain_register.rs);
 * - staff access changes and API keys (access_changes.rs): every change is a proposal that a
 *   second person approves (or the proposer, with a fresh authenticator code, when they may
 *   approve their own changes), then applied once;
 * - prices and promotions (pricing_write.rs);
 * - registry operations (registry_write.rs);
 * - market administration (auctions.rs).
 *
 * Every write carries a reason (kept in the audit log) and an Idempotency-Key, except applying an
 * access change, which is single-use by itself and may return a secret shown once.
 */

import type { CallOptions, DecimalString, ISODateTime, ItemList, Money, WriteOptions } from '../types';
import type { RoleScopeType } from './admin';
import type { PriceBook, PriceRule } from './commerce';

// ---------------------------------------------------------------------------
// Domain registration for a customer

export interface AvailabilityResult {
  name: string;
  unicode?: string;
  tld?: string;
  tld_id?: string;
  /** null: the registry could not be asked right now (`reason` says why). */
  available: boolean | null;
  /**
   * INVALID_NAME, TLD_NOT_SUPPORTED, the registry's own reason, or why it could not answer:
   * REGISTRY_NOT_CONFIGURED, REGISTRY_AUTH_FAILED, REGISTRY_ERROR, REGISTRY_TIMEOUT,
   * TLD_NOT_OFFERED_BY_REGISTRY (with `missing`, `detail`, the provider's code / message).
   */
  reason: string | null;
  missing?: string | null;
  detail?: string;
  detail_en?: string;
  provider_code?: string | null;
  provider_message?: string | null;
  /** The connection asked (links to its page). */
  registry?: import('./registry').ZoneConnectionRef;
}

/** A VNNIC promotion code held for a domain and registrant (the code itself stays on the server). */
export interface VnnicPromotionCode {
  /** `AQ******UL`. */
  code_hint: string;
  domain: string;
  zone: string;
  program: string;
  free_years: number;
  subject_id: string;
  status: 'AVAILABLE';
  /** End of the code's validity (VNNIC's expiry, else 30 minutes after it was issued). */
  expires_at: string;
  issued_at: string;
  /** An earlier check still inside its validity: VNNIC was not asked again. */
  reused: boolean;
}

export interface RegistrationQuote {
  quote_id: string;
  domain: string;
  unicode: string;
  years: number;
  subtotal: Money;
  tax: Money;
  total: Money;
  expires_at: ISODateTime;
  premium: boolean;
  discount: DecimalString | null;
  /** A VNNIC promotion code applied (program, waived fees, how it was validated). */
  registry_promotion?: { code: string; program: string; waived: string[]; max_years: number; validation: string } | null;
  /** Itemised: service line, and the TLD's mandatory / default-on add-ons (fees) with their tax. */
  lines?: import('./services').BreakdownLine[];
  /** Mock / UI helper: the add-on codes that are mandatory (cannot be left out). */
  mandatory_addons?: import('../types').AddonCode[];
  /** The TLD's connection cannot be used now (the order would be refused): why. */
  registry_problem?: import('./registry').RegistryProblem | null;
}

export interface RegistrationRequest {
  customer_id: string;
  quote_id: string;
  subject_id: string;
  nameservers: string[];
  accepted_agreement_version: string;
  reason: string;
  /** The extra data the TLD requires, `{key: value}`; a value left out is taken from the registrant where the TLD says so. */
  extra_fields?: Record<string, string>;
}

export interface RegistrationPlaced {
  order_id: string;
  domain_id: string;
  domain: string;
  workflow_id: string;
  years: number;
  status: 'PROCESSING';
}

// ---------------------------------------------------------------------------
// Staff access changes and API keys

export type AccessChangeKind =
  | 'CREATE_STAFF'
  | 'DISABLE_STAFF'
  | 'ENABLE_STAFF'
  | 'RESET_MFA'
  | 'UNLOCK_STAFF'
  | 'GRANT_ROLE'
  | 'REVOKE_ROLE'
  | 'ISSUE_API_CREDENTIAL'
  // The reporting feed (Cài đặt › Kết nối › API báo cáo; areas/reporting.ts).
  | 'CHANGE_REPORTING_CONSUMER'
  | 'ISSUE_REPORTING_KEY'
  | 'ROTATE_REPORTING_KEY'
  | 'RESET_REPORTING_SYNC';

/** The access-change kinds of the reporting feed (permission `reporting_key.manage`). */
export const REPORTING_CHANGE_KINDS: readonly AccessChangeKind[] = ['CHANGE_REPORTING_CONSUMER', 'ISSUE_REPORTING_KEY', 'ROTATE_REPORTING_KEY', 'RESET_REPORTING_SYNC'];

export type AccessChangeStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'EXECUTED' | 'CANCELLED';

export interface AccessChangeProposal {
  kind: AccessChangeKind;
  user_id?: string;
  email?: string;
  display_name?: string;
  roles?: string[];
  role_code?: string;
  scope_type?: RoleScopeType;
  scope_id?: string | null;
  owner_type?: 'CUSTOMER';
  owner_id?: string;
  expires_at?: ISODateTime | null;
  /** Reporting kinds: the place that receives data. */
  consumer_id?: string;
  /** CHANGE_REPORTING_CONSUMER: what it receives (`reporting:*`), contact data, the weekly re-send. */
  datasets?: string[];
  contacts?: boolean;
  weekly_resweep?: boolean;
  /** ISSUE_REPORTING_KEY / ROTATE_REPORTING_KEY: the key's name, use and addresses. */
  name?: string;
  purpose?: 'SCHEDULED' | 'TRIGGERED';
  allowed_cidrs?: string[];
  /** ROTATE_REPORTING_KEY: the key replaced and the days it keeps working (1, 7 or 30). */
  credential_id?: string;
  overlap_days?: number;
  /** RESET_REPORTING_SYNC: the dataset started again. */
  dataset?: string;
  reason: string;
}

/** What the change applies to, as checked and stored when it was proposed. */
export interface AccessChangeTarget {
  user_id?: string;
  email?: string;
  display_name?: string;
  roles?: string[];
  role_code?: string;
  scope_type?: RoleScopeType;
  scope_id?: string | null;
  scope_name?: string | null;
  owner_type?: 'CUSTOMER';
  owner_id?: string;
  owner_name?: string;
  expires_at?: ISODateTime | null;
  /** Reporting kinds. */
  consumer_id?: string;
  consumer_name?: string;
  datasets?: string[];
  contacts_option?: boolean;
  weekly_resweep?: boolean;
  before?: { datasets: string[]; contacts_option: boolean };
  removed?: string[];
  /** The change sends personal contact data (the approver needs `personal_data.export`). */
  contacts?: boolean;
  turns_contacts_on?: boolean;
  key?: { name: string; purpose: 'SCHEDULED' | 'TRIGGERED'; allowed_cidrs: string[]; expires_at: ISODateTime | null };
  replaces_credential_id?: string | null;
  overlap_days?: number | null;
  addresses_missing?: boolean;
  dataset?: string;
}

export interface AccessChange {
  id: string;
  kind: AccessChangeKind;
  action: 'admin.users.manage' | 'api_credential.manage' | 'reporting_key.manage';
  /** Sends personal contact data: only a second person holding `personal_data.export` approves it. */
  needs_personal_data_approver?: boolean;
  resource_type: string;
  resource_id: string;
  target: AccessChangeTarget;
  reason: string;
  status: AccessChangeStatus;
  requested_by: string;
  requested_by_name: string | null;
  requested_by_self: boolean;
  requested_at: ISODateTime;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_at: ISODateTime | null;
  decision_reason: string | null;
  self_approved: boolean;
  expires_at: ISODateTime;
  executed_at: ISODateTime | null;
  /** Another holder of the permission may approve or reject it. */
  can_decide: boolean;
  /** The caller proposed it and may approve it themself with a fresh authenticator code. */
  can_self_approve: boolean;
  can_apply: boolean;
  can_cancel: boolean;
}

export interface AccessChangeQuery {
  /** OPEN (default), CLOSED or ALL. */
  state?: 'OPEN' | 'CLOSED' | 'ALL';
  user_id?: string;
  area?: 'STAFF' | 'API_CREDENTIAL' | 'REPORTING';
  limit?: number;
}

export interface AccessChangeDecision {
  approve: boolean;
  reason: string;
  /** Current authenticator code: required to approve one's own change. */
  totp?: string;
  /**
   * Turning contact data on for a place that receives data: the approver confirms where the
   * receiving system is hosted, how long it keeps run history and who can view runs.
   */
  confirmations?: { hosting: boolean; run_history: boolean; viewers: boolean };
}

/** Applied change. The secrets are in this response only and are never shown again. */
export interface AccessChangeApplied {
  result: 'APPLIED';
  change: AccessChange;
  user_id?: string;
  /** New staff member / reset MFA: the authenticator enrolment link. */
  totp_uri?: string;
  /** New staff member, when no first password was typed: the generated one. */
  initial_password?: string | null;
  credential_id?: string;
  key_prefix?: string;
  /** New API key (`<key id>.<secret>`). */
  api_key?: string;
  /** New reporting key: the address the place reads from. */
  base_url?: string;
  changed?: boolean;
}

// ---------------------------------------------------------------------------
// Prices and promotions

/** A registry cost of a TLD. Costs are per TLD, never per connection or environment. */
export interface RegistryPriceInput {
  tld_id: string;
  operation: string;
  /** With operation `ADDON`: the add-on priced. */
  addon_code?: string | null;
  /** Add-ons: a fixed amount, or a percentage of the registry cost of the operation it comes with. */
  amount_basis?: 'FIXED' | 'PERCENT_OF_REGISTRY_COST';
  /** Add-ons: the lifecycle operation this cost is for (null = every operation). */
  for_operation?: string | null;
  years: number;
  amount: DecimalString;
  currency: string;
  /** Omitted: now. */
  effective_from?: ISODateTime;
  reason: string;
}

export interface PublishedPrice {
  id: string;
  version: number;
  effective_from: ISODateTime;
  /** The window this price closed. */
  closed_id: string | null;
}

export interface EndPriceInput {
  /** Omitted: now. */
  effective_until?: ISODateTime;
  reason: string;
}

/** A VAT rate version (`tax.manage`): the rate a product's VAT code is taxed at from `effective_from`. */
export interface TaxRateInput {
  /** Omitted: VN. */
  jurisdiction?: string;
  /** e.g. VAT10; never KCT (not taxable is a category, not a rate). */
  tax_code: string;
  /** A percentage, e.g. "10". */
  rate_percent: DecimalString;
  /** The legal basis of the rate. */
  source_reference: string;
  /** Omitted: now. */
  effective_from?: ISODateTime;
  reason: string;
}

export interface PriceBookInput {
  code: string;
  currency: string;
  applies_to: PriceBook['applies_to'];
  /** TIER books. */
  tier?: string | null;
  /** CUSTOMER_CONTRACT books. */
  customer_id?: string | null;
  status?: PriceBook['status'];
  reason: string;
}

export interface PriceRuleInput {
  tld_id?: string | null;
  operation: string;
  /** With operation `ADDON`: the add-on priced. */
  addon_code?: string | null;
  /** Tax of the line this rule prices (default VAT at the default rate). */
  tax_category?: 'VAT' | 'KCT';
  tax_code?: string | null;
  years?: number | null;
  rule_type: PriceRule['rule_type'];
  value: DecimalString;
  min_amount?: DecimalString | null;
  max_amount?: DecimalString | null;
  priority?: number;
  effective_from?: ISODateTime;
  reason: string;
}

/**
 * Batches: the rows changed in one grid (every operation of a TLD), published together — all or
 * nothing, one effective date and reason. A refused row is named in `error.details.rows`.
 */
export interface BatchRowError {
  index: number;
  operation: string;
  code: string;
  message: string;
}

export interface BatchPublished {
  effective_from: ISODateTime;
  items: { index: number; operation?: string; action?: string; id?: string; version?: number; closed_id?: string | null; ended?: string[] }[];
}

/** `POST /commerce/pricing/books/{id}/tld-prices`: a TLD's selling prices in one book. */
export interface TldPricesInput {
  tld_id: string;
  items: {
    operation: string;
    /** SET (default) a price of the TLD; USE_DEFAULT ends it (the book's default applies). */
    action?: 'SET' | 'USE_DEFAULT';
    rule_type?: PriceRule['rule_type'];
    value?: DecimalString;
    years?: number | null;
  }[];
  effective_from?: ISODateTime;
  reason: string;
}

/** `POST /commerce/pricing/registry-prices/batch`: a TLD's registry cost per operation. */
export interface RegistryCostsInput {
  tld_id: string;
  items: { operation: string; years?: number; amount: DecimalString; currency: string }[];
  effective_from?: ISODateTime;
  reason: string;
}

/** `POST /registry/tlds/{id}/addons/{code}/prices`: an add-on's cost per operation and selling price per book. */
export interface AddonPricesInput {
  costs: {
    /** Null: every operation. */
    for_operation?: string | null;
    action?: 'SET' | 'END';
    amount_basis?: 'FIXED' | 'PERCENT_OF_REGISTRY_COST';
    amount?: DecimalString;
    currency?: string;
  }[];
  rules: { price_book_id: string; action?: 'SET' | 'END'; rule_type?: PriceRule['rule_type']; value?: DecimalString }[];
  effective_from?: ISODateTime;
  reason: string;
}

export interface PromotionInput {
  /** Create only. */
  code?: string;
  discount_type: 'PERCENT' | 'FIXED';
  value: DecimalString;
  currency?: string | null;
  tld_id?: string | null;
  operation?: string | null;
  starts_at?: ISODateTime;
  ends_at: ISODateTime;
  max_redemptions?: number | null;
  /** Update only: the version read. */
  version?: number;
  reason: string;
}

// ---------------------------------------------------------------------------
// Registry operations and market

export type IssueOutcome = 'REGISTRY_CORRECT' | 'FIXED' | 'NO_ACTION';

export interface OperatorApi {
  checkDomainAvailability(body: { names: string[] }, opts?: CallOptions): Promise<{ results: AvailabilityResult[] }>;
  quoteDomainRegistration(
    body: {
      customer_id: string;
      domain: string;
      years: number;
      promotion_code?: string;
      /** Apply the VNNIC free program (id.vn / biz.vn) with the code requested for this domain (PROMO_CODE_NOT_REQUESTED without one). */
      registry_promotion?: boolean;
      /** Intended registrant: a type the zone does not allow is refused (REGISTRANT_TYPE_NOT_ALLOWED). */
      subject_id?: string;
      /** The extra data the TLD requires (`getTldExtraFields`): checked now when sent. */
      extra_fields?: Record<string, string>;
    },
    opts?: CallOptions,
  ): Promise<RegistrationQuote>;
  registerDomainForCustomer(body: RegistrationRequest, opts?: WriteOptions): Promise<RegistrationPlaced>;
  /**
   * Asks VNNIC for the free-program promotion code of `domain` for the registrant — the eligibility
   * check; a check still inside its validity is returned instead (never an older one). Errors: PROMO_REGISTRANT_INCOMPLETE (`details.missing`),
   * PROMO_CODE_REFUSED, PROMO_CODE_CHECK_UNAVAILABLE, PROMO_CODE_NOT_ELIGIBLE, REGISTRANT_TYPE_NOT_ALLOWED.
   */
  requestVnnicPromotionCode(body: { customer_id: string; domain: string; subject_id: string }, opts?: WriteOptions): Promise<VnnicPromotionCode>;

  listAccessChanges(q: AccessChangeQuery, opts?: CallOptions): Promise<ItemList<AccessChange>>;
  proposeAccessChange(body: AccessChangeProposal, opts?: WriteOptions): Promise<AccessChange>;
  /** The existing approvals endpoint (`POST /approvals/{id}/decision`). */
  decideAccessChange(id: string, body: AccessChangeDecision, opts?: WriteOptions): Promise<{ id: string; status: string }>;
  applyAccessChange(id: string, body: { password?: string }, opts?: CallOptions): Promise<AccessChangeApplied>;
  cancelAccessChange(id: string, body: { reason: string }, opts?: WriteOptions): Promise<AccessChange>;
  revokeApiCredential(id: string, body: { reason: string }, opts?: WriteOptions): Promise<{ id: string; status: 'REVOKED' }>;

  publishRegistryPrice(body: RegistryPriceInput, opts?: WriteOptions): Promise<PublishedPrice>;
  endRegistryPrice(id: string, body: EndPriceInput, opts?: WriteOptions): Promise<{ id: string; effective_until: ISODateTime }>;
  createPriceBook(body: PriceBookInput, opts?: WriteOptions): Promise<{ id: string; status: PriceBook['status'] }>;
  setPriceBookStatus(id: string, body: { status: PriceBook['status']; reason: string }, opts?: WriteOptions): Promise<{ id: string; status: PriceBook['status'] }>;
  publishPriceRule(bookId: string, body: PriceRuleInput, opts?: WriteOptions): Promise<PublishedPrice>;
  endPriceRule(id: string, body: EndPriceInput, opts?: WriteOptions): Promise<{ id: string; effective_until: ISODateTime }>;
  publishTldPrices(bookId: string, body: TldPricesInput, opts?: WriteOptions): Promise<BatchPublished>;
  publishRegistryCosts(body: RegistryCostsInput, opts?: WriteOptions): Promise<BatchPublished>;
  publishTldAddonPrices(tldId: string, code: string, body: AddonPricesInput, opts?: WriteOptions): Promise<BatchPublished>;
  createPromotion(body: PromotionInput, opts?: WriteOptions): Promise<{ id: string; code: string }>;
  updatePromotion(id: string, body: PromotionInput, opts?: WriteOptions): Promise<{ id: string; version: number }>;
  disablePromotion(id: string, body: { reason: string }, opts?: WriteOptions): Promise<{ id: string; disabled: true }>;
  publishTaxRate(body: TaxRateInput, opts?: WriteOptions): Promise<PublishedPrice>;
  endTaxRate(id: string, body: EndPriceInput, opts?: WriteOptions): Promise<{ id: string; effective_until: ISODateTime }>;
  /** The VAT code of a price line that names none (TLD and add-on prices). */
  setTaxDefault(body: { tax_code: string; reason: string }, opts?: WriteOptions): Promise<{ jurisdiction: string; tax_code: string; previous: string | null }>;

  resolveReconciliationIssue(
    id: string,
    body: { outcome: IssueOutcome; reason: string },
    opts?: WriteOptions,
  ): Promise<{ id: string; state: string; outcome: IssueOutcome; domain_reconcile_scheduled: boolean }>;
  acknowledgePollMessage(id: string, body: { reason: string }, opts?: CallOptions): Promise<{ id: string; acknowledged: true }>;
  markPollMessageProcessed(id: string, body: { reason: string }, opts?: WriteOptions): Promise<{ id: string; processed: true }>;

  cancelBackorder(id: string, body: { reason: string }, opts?: WriteOptions): Promise<{ id: string; status: string }>;
  openAuction(id: string, body: { reason: string }, opts?: WriteOptions): Promise<{ id: string; status: string }>;
  closeAuction(
    id: string,
    body: { reason: string },
    opts?: WriteOptions,
  ): Promise<{ id: string; status: string; winner_customer_id: string | null; final_price: DecimalString | null; unsold_reason: string | null }>;
}
